import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  BookingInfo,
  CalculateAmount,
  Cart,
  TransportContext,
} from '../lib/api/types'

const emptyCart: Cart = {
  transportId: null,
  fromDay: null,
  fromHour: null,
  toDay: null,
  durationId: null,
  rateId: null,
  conventionId: null,
  serviceIdList: null,
  couponCode: null,
  paymentModeId: null,
}

const emptyBookingInfo: BookingInfo = {
  name: '',
  lastname: '',
  email: '',
  phoneNumber: '',
  country: 'IT',
  licensePlate: '',
  privacy01: '',
  privacy02: '',
  privacy03: '',
  requireBilling: '',
}

export interface ConfirmationResult {
  code: string
  title?: string
  text?: string
}

interface BookingState {
  contexts: TransportContext[]
  cart: Cart
  calculatedInfo: CalculateAmount | null
  bookingInfo: BookingInfo
  couponLabel: string | null
  conventionLabel: string | null
  // Set on a successful booking; read by the confirmation step. Transient (not persisted).
  confirmation: ConfirmationResult | null

  setContexts: (c: TransportContext[]) => void
  setTransport: (transportId: number) => void
  setDates: (fromDay: string, fromHour: string, toDay: string) => void
  setRate: (rateId: number | null) => void
  setConvention: (conventionId: number | null, label?: string | null) => void
  toggleService: (serviceId: number) => void
  setCoupon: (code: string | null, label: string | null) => void
  setPaymentMode: (paymentModeId: number | null) => void
  setCalculated: (c: CalculateAmount | null) => void
  setBookingInfo: (patch: Partial<BookingInfo>) => void
  setConfirmation: (c: ConfirmationResult | null) => void
  reset: () => void

  contextFor: (transportId: number | null) => TransportContext | undefined
}

export const useBooking = create<BookingState>()(
  persist(
    (set, get) => ({
      contexts: [],
      cart: { ...emptyCart },
      calculatedInfo: null,
      bookingInfo: { ...emptyBookingInfo },
      couponLabel: null,
      conventionLabel: null,
      confirmation: null,

      setContexts: (contexts) => set({ contexts }),

      setTransport: (transportId) =>
        set((s) => ({
          // changing transport invalidates rate/services selection
          cart: { ...s.cart, transportId, rateId: null, serviceIdList: null, conventionId: null },
          conventionLabel: null,
        })),

      setDates: (fromDay, fromHour, toDay) =>
        set((s) => ({ cart: { ...s.cart, fromDay, fromHour, toDay, rateId: null } })),

      setRate: (rateId) => set((s) => ({ cart: { ...s.cart, rateId } })),

      setConvention: (conventionId, label = null) =>
        set((s) => ({ cart: { ...s.cart, conventionId }, conventionLabel: conventionId ? label : null })),

      toggleService: (serviceId) =>
        set((s) => {
          const list = new Set(s.cart.serviceIdList ?? [])
          if (list.has(serviceId)) list.delete(serviceId)
          else list.add(serviceId)
          const arr = [...list]
          return { cart: { ...s.cart, serviceIdList: arr.length ? arr : null } }
        }),

      setCoupon: (couponCode, couponLabel) =>
        set((s) => ({ cart: { ...s.cart, couponCode }, couponLabel })),

      setPaymentMode: (paymentModeId) => set((s) => ({ cart: { ...s.cart, paymentModeId } })),

      setCalculated: (calculatedInfo) => set({ calculatedInfo }),

      setBookingInfo: (patch) => set((s) => ({ bookingInfo: { ...s.bookingInfo, ...patch } })),

      setConfirmation: (confirmation) => set({ confirmation }),

      reset: () =>
        set({
          cart: { ...emptyCart },
          calculatedInfo: null,
          bookingInfo: { ...emptyBookingInfo },
          couponLabel: null,
          conventionLabel: null,
          confirmation: null,
        }),

      contextFor: (transportId) => get().contexts.find((c) => c.transport.id === transportId),
    }),
    {
      name: 'gsm-booking',
      partialize: (s) => ({ cart: s.cart, bookingInfo: s.bookingInfo, couponLabel: s.couponLabel, conventionLabel: s.conventionLabel }),
    },
  ),
)

/** True when the cart has enough to compute a price. */
export function cartIsCalculable(cart: Cart): boolean {
  return Boolean(cart.transportId && cart.fromDay && cart.fromHour && (cart.toDay || cart.rateId))
}
